<script setup lang="ts">
import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppButton, AppSvgIcon } from '@libs/ui';

interface HotelSelectionNoResultsProps {
  hasActiveHotelFilters: boolean;
}

const props = defineProps<HotelSelectionNoResultsProps>();

interface HotelSelectionNoResultsEmits {
  (eventName: 'reset-filters'): void;
}

const emit = defineEmits<HotelSelectionNoResultsEmits>();

const browserStore = useBrowserStore();
</script>

<template>
  <div class="hotel-selection-no-results">
    <AppSvgIcon
      class="icon"
      name="searchOff"
      size="36"
      color="color-brand-violet"
    />
    <div class="content">
      <div class="title">{{ $t('no_results_banner.sorry_no_results') }}</div>

      <p class="try-the-following">
        {{ $t('no_results_banner.try_the_following') }}
      </p>
      <ul class="list">
        <li v-if="props.hasActiveHotelFilters">
          {{ $t('no_results_banner.change_your_filters') }}
        </li>
        <li v-if="browserStore.isLargerThan1199px">
          {{ $t('no_results_banner.zoom_out_on_the_map') }}
        </li>
        <li>
          {{ $t('no_results_banner.search_for_different_location') }}
        </li>
      </ul>
    </div>
    <AppButton
      v-if="props.hasActiveHotelFilters"
      type="secondary"
      state="enabled"
      size="medium"
      @click="emit('reset-filters')"
    >
      {{ $t('planner.reset_hotel_filters') }}
    </AppButton>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-no-results {
  background-color: var(--color-white);
  padding: var(--spacing-24);
  margin: 0 var(--spacing-16) var(--spacing-16) var(--spacing-16);
  border: 1px solid var(--color-neutral-30);
  border-radius: var(--border-radius-8);
  height: 100%;
  gap: var(--spacing-8);
  display: flex;
  align-items: center;
  @include for-less-or-equal-than-599-px {
    flex-direction: column;
  }
  .content {
    display: flex;
    flex-direction: column;
    flex: 1;

    @include font-label-s-light;
    @include for-less-or-equal-than-599-px {
      align-items: center;
      margin-bottom: var(--spacing-8);
    }
    .try-the-following {
      margin: 0;
      margin-bottom: var(--spacing-2);
    }

    li {
      padding-left: 0;
    }
    .list {
      margin: 0;
      padding-left: var(--spacing-8);
      list-style: none;
      @include for-less-or-equal-than-599-px {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
    }

    .list li {
      display: flex;
      align-items: center;
    }

    .list li::before {
      content: '•';
      font-size: var(--spacing-8);
      margin-right: var(--spacing-8);
    }
    .title {
      @include font-heading-6;
      margin-bottom: var(--spacing-8);
      @include for-less-or-equal-than-599-px {
        margin-bottom: var(--spacing-16);
      }
    }
  }

  @include for-more-or-equal-than-1200-px {
    margin: var(--spacing-24) 0;
  }

  @include for-more-or-equal-than-1535-px {
    margin-top: 0;
  }
}
</style>
